import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Globe2, LayoutGrid, FileText, PlusSquare, LogOut, Landmark, AlertTriangle } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = { operator: "Postal Operator", vendor: "Vendor / Bidder", admin: "Administrator" };

const navCls = ({ isActive }) =>
  `flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors duration-200 border-b-2 ${
    isActive ? "border-[#0050A0] text-[#0050A0]" : "border-transparent text-slate-500 hover:text-slate-900"
  }`;

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const role = user.role;
  const [creditAlert, setCreditAlert] = useState(null);
  const handleLogout = async () => { await logout(); navigate("/login"); };

  useEffect(() => {
    if (role === "admin") {
      axios.get(`${API}/admin/tavily-usage`).then((r) => { if (r.data.alert) setCreditAlert(r.data); }).catch(() => {});
    }
  }, [role]);
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-10">
            <Link to="/" data-testid="header-logo-link" className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-[#0050A0] flex items-center justify-center">
                <Globe2 className="w-5 h-5 text-white" aria-hidden="true" />
              </div>
              <span className="font-serif text-xl font-bold text-slate-900 tracking-tight">makemytender<span className="text-[#009EDB]">.com</span></span>
            </Link>
            <nav className="hidden md:flex items-center h-16">
              <NavLink to="/dashboard" data-testid="nav-dashboard-link" className={navCls}>
                <LayoutGrid className="w-4 h-4" aria-hidden="true" /> Dashboard
              </NavLink>
              <NavLink to="/tenders" end data-testid="nav-tenders-link" className={navCls}>
                <FileText className="w-4 h-4" aria-hidden="true" /> Tenders
              </NavLink>
              <NavLink to="/countries" data-testid="nav-countries-link" className={navCls}>
                <Landmark className="w-4 h-4" aria-hidden="true" /> Country Intel
              </NavLink>
              {role !== "vendor" && (
                <NavLink to="/tenders/new" data-testid="nav-create-tender-link" className={navCls}>
                  <PlusSquare className="w-4 h-4" aria-hidden="true" /> Float Tender
                </NavLink>
              )}
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium text-slate-900 leading-tight" data-testid="header-user-name">{user.name}</p>
              <p className="text-[11px] uppercase tracking-[0.15em] text-[#0050A0]" data-testid="header-user-role">{ROLE_LABELS[role]}</p>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="inline-flex items-center gap-2 border border-slate-300 text-slate-600 px-4 py-2 text-xs font-semibold hover:bg-slate-900 hover:text-white transition-colors duration-200"
            >
              <LogOut className="w-3.5 h-3.5" aria-hidden="true" /> Sign Out
            </button>
          </div>
        </div>
      </header>
      {creditAlert && (
        <div className="bg-red-600 text-white" data-testid="credit-alert-banner">
          <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center gap-3 text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
            <span>
              {creditAlert.paused ? (
                <><strong>Live lookups auto-paused:</strong> the {creditAlert.monthly_limit}-credit monthly Tavily limit is reached. Lookups resume next month; profiles generate from model knowledge meanwhile.</>
              ) : (
                <><strong>Tavily credit alert:</strong> {creditAlert.credits_used} of {creditAlert.monthly_limit} search credits used this month (threshold {creditAlert.alert_threshold}). Lookups auto-pause at {creditAlert.monthly_limit}.</>
              )}
            </span>
          </div>
        </div>
      )}
      <main className="max-w-7xl mx-auto px-6 py-10">
        <Outlet />
      </main>
    </div>
  );
}
